// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.D3DrawUtils.js" company="Izenda">
// Copyright (c) 2015 Izenda, Inc.  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and  International
// Copyright Laws. Unauthorized reproduction,  reverse-engineering, and
// distribution of all or any portion of  the code contained in this file is
// strictly prohibited and may  result in severe civil and criminal penalties
// and will be  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES  ARE CONFIDENTIAL AND
// PROPRIETARY TRADE  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
// LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET  CONTROLS AS
// PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED  FILES OR ANY
// PORTION OF ITS CONTENTS SHALL AT NO TIME BE  COPIED, TRANSFERRED, SOLD,
// DISTRIBUTED, OR OTHERWISE MADE  AVAILABLE TO OTHER INDIVIDUALS WITHOUT
// EXPRESS WRITTEN CONSENT  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  ADDITIONAL
// RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import { d3 } from './Izenda.D3Wrapper';
import { get, uniqueId, toNumber } from 'lodash/fp';
import tinycolor from 'tinycolor2';
import raf from 'raf';
import { ANIMATION_PORTION } from 'logic/data/Izenda.D3VizEngineConstants';
import { MappingValues } from 'logic/data/Izenda.ChartDefaultConfig';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import { UNDEFINED_VALUE } from 'logic/data/Izenda.UIConstants';
import convertToDateTimeFormat from 'share/utils/Izenda.ConvertToDateTimeFormat';

const { LABEL_ROTATION_VALUES } = MappingValues;

/**
 * Get text color that contrast with background color
 * @param {String} backgroundColor
 */
export function getContrastTextColor(backgroundColor: String) {
  const color = tinycolor(backgroundColor).toRgb();
  const yiq = (color.r * 299 + color.g * 587 + color.b * 114) / 1000;
  return yiq >= 128 ? '#000' : '#FFF';
}

/**
 * Check if this node is leaf node
 * @param {Node} node 
 * @param {Number} treeHeight 
 */
export function isLeafNode(node, treeHeight: Number) {
  return node.depth === treeHeight;
}

/**
 * Add clip path to defs element
 * @param {d3.Selection<SVGElement>} svg
 * @param {Number} width
 * @param {Number} height
 */
export function addClipPath(svg: d3.Selection<SVGElement>, width: Number, height: Number) {
  const id = uniqueId('clipPath-');
  let defs = svg.select('defs');
  if (defs.empty()) {
    defs = svg.append('defs');
  }

  const rect = defs
    .append('clipPath')
    .attr('id', id)
    .append('rect')
    .attr('height', height)
    .attr('width', width)
    .attr('fill', 'none');

  return { id, rect };
}

/**
 * Begin the show plot animation
 * @param {d3.Selection<SVGRect>} clipPathRect
 * @param {Number} plotWidth
 */
export function beginPlotAnimation(clipPathRect: d3.Selection<SVGRect>, plotWidth: Number) {
  let rafId = null;
  const animateClipRect = () => {
    const currentWidth = toNumber(clipPathRect.attr('width'));
    if (currentWidth < plotWidth) {
      const newWidth = Math.min(currentWidth + plotWidth / ANIMATION_PORTION, plotWidth);
      clipPathRect.attr('width', newWidth);
      rafId = raf(animateClipRect);
    } else {
      raf.cancel(rafId);
    }
  };
  rafId = raf(animateClipRect);
}

/**
 * Check if 2 rects are intersect
 * @param {*} x1
 * @param {*} y1
 * @param {*} w1
 * @param {*} h1
 * @param {*} x2
 * @param {*} y2
 * @param {*} w2
 * @param {*} h2
 */
function isIntersectRect(x1, y1, w1, h1, x2, y2, w2, h2) {
  return !(x2 > x1 + w1 || x2 + w2 < x1 || y2 > y1 + h1 || y2 + h2 < y1);
}

/**
 * Hide overlapping labels
 * @param {Array} labelNodes
 * @param {Number} rotateDegree
 */
export function hideOverlappingLabels(labelNodes: Array<SVGTextElement>) {
  for (let i = 0; i < labelNodes.length; i++) {
    const label1 = labelNodes[i];
    const labelNode1 = d3.select(label1);
    const box1 = label1 && calculateBoxByRotateDegree(label1.getBBox(), labelNode1.datum().rotation);

    for (let j = i + 1; j < labelNodes.length; j++) {
      const label2 = labelNodes[j];
      const labelNode2 = d3.select(label2);
      const box2 = label2 && calculateBoxByRotateDegree(label2.getBBox(), labelNode2.datum().rotation);

      if (
        label1 &&
        label2 &&
        label1 !== label2 &&
        labelNode1.attr('visibility') === 'visible' &&
        labelNode2.attr('visibility') === 'visible' &&
        isIntersectRect(box1.x, box1.y, box1.width, box1.height, box2.x, box2.y, box2.width, box2.height)
      ) {
        labelNode2.attr('visibility', 'hidden');
      }
    }
  }
}

/**
 * Calculate new label box after rotation
 * @param {Object} box
 * @param {Number} rotateDegree
 * @returns {Object}
 */
export function calculateBoxByRotateDegree(box, rotateDegree) {
  switch (rotateDegree) {
    case LABEL_ROTATION_VALUES.izTextup:
    case LABEL_ROTATION_VALUES.izTextdown:
      return {
        x: box.x + (box.width - box.height) / 2,
        y: box.y + (box.height - box.width) / 2,
        width: box.height,
        height: box.width
      };
    case LABEL_ROTATION_VALUES.izCounterclockwise:
    case LABEL_ROTATION_VALUES.izClockwise: {
      const newLength = Math.cos(Math.PI / 4) * (box.width + box.height);
      return {
        x: box.x + (box.width - newLength) / 2,
        y: box.y + (box.height - newLength) / 2,
        width: newLength,
        height: newLength
      };
    }
    default:
      return box;
  }
}

/**
 *
 * @param {any} data
 * @param {any} field
 * @param {Function} onDrilldownFn
 */
export function onDrilldown(data: any, serieOpt: any, onDrilldownFn: Function) {
  const separatorField = get('separatorField', serieOpt);
  const record = get('point.record', data);
  const originalValue = ReportPartUtils.getOriginalValue(separatorField, record);
  onDrilldownFn &&
    onDrilldownFn(
      {
        field: serieOpt.nameField,
        value: data.point.originalNameValue,
        record: data.point.record
      },
      {
        field: serieOpt.separatorField,
        value: serieOpt.separatorValue === UNDEFINED_VALUE ? '' : originalValue
      }
    );
}

/**
 * Set click event for labels that have customURL, subReport or embeddedJavascript
 * @param {d3.Selection} labels
 * @param {Object} izendaSeriesConfig
 * @param {Function} pointAction
 */
export function setLabelsClick(labels: d3.Selection, izendaSeriesConfig: Object, pointAction: Function) {
  labels.on('click.textLink', ({ point, serie }) => {
    const fieldNameValue = get('labelName', point) || get('labelName', serie);
    const { customURL, subReport, embeddedJavascript } = izendaSeriesConfig[fieldNameValue];
    if (!customURL && !subReport && !embeddedJavascript) {
      return;
    }

    const separatorColumnName = get('separatorField.columnName', serie);
    const separatorValue = get('separatorValue', serie);
    let pointName = get('name', point);
    const isDateTimeType = get('nameField.reportPartElm.isDateTimeType', serie);
    const fieldFormatData = get('nameField.reportPartElm.fieldFormatData', serie);

    pointName = isDateTimeType ? convertToDateTimeFormat(fieldFormatData, pointName) : pointName;

    pointAction(
      izendaSeriesConfig[fieldNameValue],
      [
        {
          key: fieldNameValue,
          value: pointName
        }
      ],
      separatorColumnName && {
        separatorColumnName,
        separatorValue
      }
    );
  });
}



// WEBPACK FOOTER //
// ./ui/share/components/charting/d3/Izenda.D3DrawUtils.js